import React, { useCallback, useState } from "react";
import classNames from "classnames/bind";
import Dropdown from "components/Forms/Inputs/Dropdown";
import TabbedButton, {
  TabbedButtonMood,
  TabbedButtonType,
} from "components/TabbedButton";
import {
  MedicaidRejectionReasonCode,
  MedicaidRejectionFeedback,
} from "modules/medicaid/types";
import { REJECTION_REASON_OPTIONS } from "./constants";
import { RejectLeadModalContentProps } from "./types";

import styles from "./styles.module.scss";

const cx = classNames.bind(styles);

const RejectLeadModalContent: React.FC<RejectLeadModalContentProps> = ({
  onDecline,
  controls,
}) => {
  const [reasonCode, setReasonCode] = useState<
    MedicaidRejectionReasonCode | ""
  >("");
  const [clientSafeReason, setClientSafeReason] = useState("");
  const [busy, setBusy] = useState(false);
  const canSubmit = reasonCode !== "";

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || busy) return;
    const feedback: MedicaidRejectionFeedback = {
      reasonCode: reasonCode as MedicaidRejectionReasonCode,
      clientSafeReason:
        clientSafeReason.trim().length > 0
          ? clientSafeReason.trim()
          : undefined,
    };
    setBusy(true);
    try {
      await onDecline(feedback);
      controls.close();
    } finally {
      setBusy(false);
    }
  }, [canSubmit, busy, reasonCode, clientSafeReason, onDecline, controls]);

  return (
    <div className={cx("reject-lead-modal")}>
      <div className={cx("reject-lead-modal__field")}>
        <label
          className={cx("reject-lead-modal__label")}
          htmlFor="reject-reason"
        >
          Reason for declining{" "}
          <span className={cx("reject-lead-modal__required")}>*</span>
        </label>
        <Dropdown
          name="reject-reason"
          id="reject-reason"
          placeholder="Select a reason"
          value={reasonCode}
          onChange={(v) =>
            setReasonCode((v as MedicaidRejectionReasonCode) || "")
          }
          options={REJECTION_REASON_OPTIONS}
        />
      </div>

      <div className={cx("reject-lead-modal__field")}>
        <label
          className={cx("reject-lead-modal__label")}
          htmlFor="client-safe-reason"
        >
          Message to client (optional)
        </label>
        <textarea
          id="client-safe-reason"
          className={cx("reject-lead-modal__textarea")}
          placeholder="Optional message the client will see (e.g. in their email)."
          value={clientSafeReason}
          onChange={(e) => setClientSafeReason(e.target.value)}
          rows={3}
        />
      </div>

      <div className={cx("reject-lead-modal__footer")}>
        <TabbedButton
          label="Decline Lead"
          mood={TabbedButtonMood.NEGATIVE}
          style={TabbedButtonType.PRIMARY}
          onClick={handleSubmit}
          disabled={!canSubmit || busy}
        />
      </div>
    </div>
  );
};

export default RejectLeadModalContent;
